import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../../util/reactIntl';

import { ExternalLink, NamedLink } from '../../../../../components';

import css from './PriorityLinks.module.css';

/**
 * Create component that shows only a single "Post a new listing" link.
 *
 * @param {*} props contains customLinksMenuClass
 * @returns div with only one link inside.
 */
export const CreateListingMenuLink = props => {
  return (
    <div className={props.customLinksMenuClass}>
      <NamedLink name="NewListingPage" className={classNames(css.priorityLink, css.highlight)}>
        <span className={css.priorityLinkLabel}>
          <FormattedMessage id="TopbarDesktop.createListing" />
        </span>
      </NamedLink>
    </div>
  );
};

/**
 * Link component that can be used on TopbarDesktop.
 *
 * @param {*} props containing linkConfig including resolved 'route' params for NamedLink.
 * @returns NamedLink or ExternalLink component based on config.
 */
const PriorityLink = ({ linkConfig }) => {
  const { text, type, href, route, highlight } = linkConfig;
  const classes = classNames(css.priorityLink, { [css.highlight]: highlight });
  const id = `priority-link-${text.toLowerCase().replace(/ /g, '-')}`;

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    return (
      <NamedLink name={name} params={params} to={to} className={classes} id={id}>
        <span className={css.priorityLinkLabel}>{text}</span>
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={classes}>
      <span className={css.priorityLinkLabel}>{text}</span>
    </ExternalLink>
  );
};

/**
 * Create priority links, which are visible on the desktop layout on the Topbar.
 * If space is limited, this doesn't include anything to the Topbar.
 *
 * @param {*} props contains links array and setLinks function
 * @returns list of priority links.
 */
const PriorityLinks = props => {
  const containerRef = useRef(null);

  // With this useEffect, we measure the widths of each rendered priority link
  // This is done before the real rendering and it's done outside the viewport.
  // Re-run when links are reset without widths (e.g. create-listing visibility change).
  useEffect(() => {
    const container = containerRef.current;
    // "Measured" means a number was recorded, not a truthy one. A measured 0 used to read as
    // unmeasured, which made this effect call setLinks with a fresh array, re-run on the
    // changed dependency, measure 0 again, and loop until React gave up.
    const isMeasured = typeof props.links?.[0]?.width === 'number';
    // TopbarDesktop is display:none below the desktop breakpoint, and every link measures 0
    // there. offsetParent is null for an element inside display:none, so skip measuring
    // until it is actually laid out; the priorityLinks dependency re-runs this effect when
    // the ResizeObserver regroups links after the container becomes visible.
    const isLaidOut = !!container && container.offsetParent !== null;
    if (isLaidOut && props.links?.length > 0 && !isMeasured) {
      const linksFromRenderedWrapper = [...container.childNodes];
      let cumulatedWidth = 0;
      // Generate an array of link configs with width & cumulatedWidth included
      const linksWithWidths = props.links.reduce((links, l, i) => {
        const width = linksFromRenderedWrapper[i].offsetWidth;
        cumulatedWidth = cumulatedWidth + width;
        return [...links, { ...l, width, cumulatedWidth }];
      }, []);
      props.setLinks(linksWithWidths);
    }
  }, [props.links, props.setLinks, props.priorityLinks]);

  const { links, priorityLinks } = props;
  const isMeasured = links?.[0]?.width && (priorityLinks.length === 0 || priorityLinks?.[0]?.width);
  const styleWrapper = !!isMeasured
    ? {}
    : {
        style: {
          position: 'absolute',
          top: '-2000px',
          left: '-2000px',
          width: '100%',
          height: 'var(--topbarHeightDesktop)',
          display: 'flex',
          flexDirection: 'row',
        },
      };
  const linkConfigs = isMeasured ? priorityLinks : links;

  // Always render inline in the Topbar tree so SSR and the initial client render match.
  // (Portaling to document.body caused hydration mismatches.)
  return (
    <ul className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
      {linkConfigs.map((linkConfig, index) => {
        return (
          <li key={`${linkConfig.text}_${index}`} className={css.priorityLinkItem}>
            <PriorityLink linkConfig={linkConfig} />
          </li>
        );
      })}
    </ul>
  );
};

export default PriorityLinks;
